<template>
  <!-- エラー時 -->
  <div v-if="hasError" class="search-results search-results--error">
    <div class="search-results__close-area">
      <span
        class="back-link"
        role="button"
        tabindex="0"
        @click="handleClose"
        @keydown.enter="handleClose"
        @keydown.space="handleClose"
      >
        × 閉じる
      </span>
    </div>
    <div class="search-results__error">
      <div class="search-results__error-icon">
        <i class="material-icons" aria-hidden="true">error_outline</i>
      </div>
      <p class="search-results__error-message">
        検索できませんでした。もう一度お試しください。
      </p>
      <button
        type="button"
        class="button button--primary button--lg"
        @click="handleRetry"
      >
        <b>もう一度検索する</b>
      </button>
    </div>
  </div>

  <!-- 0件時 -->
  <div v-else-if="!hasResults" class="search-results search-results--empty">
    <EmptyPane
      @close="handleClose"
      @retry="handleRetry"
    />
    <!-- 検索条件変更アコーディオン（0件時も表示） -->
    <div class="search-results__accordion-wrapper">
      <SearchConditionAccordion
        v-model="formData"
        :is-loading="isLoading"
        @search="handleReSearch"
      />
    </div>
  </div>

  <!-- 結果あり -->
  <div v-else class="search-results">
    <!-- 閉じるリンク -->
    <div class="search-results__close-area">
      <span
        class="back-link"
        role="button"
        tabindex="0"
        @click="handleClose"
        @keydown.enter="handleClose"
        @keydown.space="handleClose"
      >
        × 閉じる
      </span>
    </div>

    <!-- ヘッダー -->
    <div class="search-results__header">
      <div class="search-results__doctor-icon">
        <img
          src="~green/icon/icon_doctor_simple.svg"
          alt=""
          class="search-results__doctor-image"
        />
      </div>
      <h2 class="search-results__title">
        <span class="search-results__keyword">{{ keyword }}</span
        >に関する
        <span class="search-results__count">{{ results.length }}</span>
        件の実際の相談事例が見つかりました。
      </h2>
    </div>

    <!-- メッセージパネル -->
    <div class="search-results__message-panel">
      <p>ご不安な中、詳しい状況を教えてくださりありがとうございます。</p>
      <p>{{ keyword }}に関して、あなたと状況が近い事例をご紹介します。</p>
      <p>
        似ている症状や経過がないか、ご自身の状況と照らし合わせてみてください。
      </p>
    </div>

    <!-- セクションタイトル -->
    <h3 class="search-results__section-title">あなたに近い相談事例</h3>
    <p class="search-results__description">
      ご入力内容（年齢・性別・回答内容）に基づき、関連度の高い順に表示しています。
    </p>

    <!-- 結果リスト -->
    <ul role="list" class="search-results__list">
      <li
        v-for="(result, index) in results"
        :key="result.topicId"
        role="listitem"
        class="search-results__item"
      >
        <a
          :href="result.url"
          target="_blank"
          rel="noopener noreferrer"
          class="search-results__card"
          @click="sendGAEvent('問診LP', 'click', `inquiry_flow_result_${index + 1}_${result.topicId}`)"
        >
          <div class="search-results__card-header">
            <span
              v-if="getAttributeLabel(result)"
              class="search-results__attribute-label"
            >
              {{ getAttributeLabel(result) }}
            </span>
            <RelevanceBadge :rank="index + 1" />
          </div>
          <h4 class="search-results__card-title">{{ result.title }}</h4>
          <p class="search-results__card-snippet">{{ result.snippet }}</p>
        </a>
      </li>
    </ul>

    <!-- 検索条件変更アコーディオン -->
    <SearchConditionAccordion
      v-model="formData"
      :is-loading="isLoading"
      @search="handleReSearch"
    />

    <!-- もっとみるボタン -->
    <a
      :href="moreUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="button button--primary-outline button--full button--lg search-results__more-button"
      @click="sendGAEvent('問診LP', 'click', 'inquiry_flow_more_button')"
    >
      もっとみる
    </a>

    <!-- フィードバックパネル -->
    <div class="search-results__feedback-panel">
      <div class="search-results__feedback-icon">
        <img
          src="~green/icon/inquiry_lp/balloon.svg"
          alt=""
          class="search-results__feedback-icon-image"
        />
      </div>
      <h3 class="search-results__feedback-title">
        ぴったりな事例は見つかりましたか？
      </h3>
      <p class="search-results__feedback-text">
        「自分の症状とは少し違う気がする...」<br />
        そう感じた場合は、特有の症状かもしれません。<br />
        あなた個人の状況を、医師に直接聞いてみませんか？
      </p>
      <a
        :href="consultUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="button button--primary button--lg button--flex search-results__consult-button"
        @click="sendGAEvent('問診LP', 'click', 'inquiry_flow_consult_button')"
      >
        <b>ネットで医師相談</b>
        <span class="material-icons" aria-hidden="true">chevron_right</span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import RelevanceBadge from '../../similar_search/components/RelevanceBadge.vue';
import EmptyPane from './EmptyPane.vue';
import SearchConditionAccordion from './SearchConditionAccordion.vue';
import type { SimilarSearchResult, InquiryFormData } from '../types';

const props = defineProps<{
  results: SimilarSearchResult[];
  hasError: boolean;
  keyword: string;
  isLoading: boolean;
}>();

const emit = defineEmits<{
  close: [];
  retry: [];
  reSearch: [];
}>();

// formData を v-model として管理
const formData = defineModel<InquiryFormData>({ required: true });

// 検索条件変更時のハンドラ
const handleReSearch = () => {
  emit('reSearch');
};

// もっとみる URL
const moreUrl = computed(() => {
  return `/search/topics?q=${encodeURIComponent(props.keyword)}`;
});

// 医師相談 URL
const consultUrl = '/topics/add-form';

// 結果があるかどうか
const hasResults = computed(() => props.results.length > 0);

// Google Analytics イベント送信
const sendGAEvent = (category: string, action: string, label: string) => {
  window.sendGAEvent(category, action, label);
};

const handleClose = () => {
  emit('close');
};

const handleRetry = () => {
  emit('retry');
};

// 年代のラベルマップ
const AGE_LABELS: Record<string, string> = {
  INFANTS: '乳幼児',
  UNDER_10: '10歳未満',
  RANGE_10_19: '10代',
  RANGE_20_29: '20代',
  RANGE_30_39: '30代',
  RANGE_40_49: '40代',
  RANGE_50_59: '50代',
  RANGE_60_69: '60代',
  OVER_70: '70代以上'
};

// 性別のラベルマップ
const GENDER_LABELS: Record<string, string> = {
  F: '女性',
  M: '男性'
};

// 年代・性別のラベルを生成
const getAttributeLabel = (result: SimilarSearchResult): string | null => {
  const parts: string[] = [];

  if (result.targetAge && AGE_LABELS[result.targetAge]) {
    parts.push(AGE_LABELS[result.targetAge]);
  }

  if (result.targetGender && GENDER_LABELS[result.targetGender]) {
    parts.push(GENDER_LABELS[result.targetGender]);
  }

  return parts.length > 0 ? parts.join('') : null;
};
</script>

<style lang="scss" scoped>
@import '~green/_variables.scss';

.search-results {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  &__doctor-icon {
    width: 80px;
    height: 80px;
    margin-bottom: 16px;
  }

  &__doctor-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__title {
    font-size: 1.125rem;
    font-weight: bold;
    color: $base-font-color;
    line-height: 1.5;
  }

  &__keyword {
    color: $primary-color;
  }

  &__count {
    color: $primary-color;
    font-size: 1.5rem;
  }

  &__message-panel {
    background-color: $section-background-color;
    border-radius: $border-radius-base;
    padding: 16px;
    font-size: 0.875rem;
    line-height: 1.6;
    color: $base-font-color;

    p {
      margin: 0 0 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__section-title {
    font-size: 1rem;
    font-weight: bold;
    color: $base-font-color;
    padding-left: 8px;
    border-left: 3px solid $primary-color;
    margin: 8px 0;
  }

  &__description {
    font-size: 0.8125rem;
    font-weight: normal;
    color: $tertiary-color;
    margin: 0;
    line-height: 1.5;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    margin: 0;
  }

  &__card {
    display: block;
    background-color: #fff;
    border: 1px solid $base-border-color;
    border-radius: $border-radius-base;
    padding: 16px;
    text-decoration: none;
    color: inherit;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:focus {
      outline: 2px solid $primary-color;
      outline-offset: 2px;
    }
  }

  &__card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  &__attribute-label {
    font-size: 0.75rem;
    color: $tertiary-color;
    background-color: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
  }

  &__card-title {
    font-size: 1rem;
    font-weight: bold;
    color: $base-font-color;
    margin: 0 0 8px;
    line-height: 1.4;
    word-break: break-all;
  }

  &__card-snippet {
    font-size: 0.875rem;
    color: $tertiary-color;
    margin: 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
  }

  &__more-button {
    margin-top: 8px;
  }

  &__feedback-panel {
    background-color: rgba($primary-color, 0.1);
    border-radius: $border-radius-base;
    padding: 24px 16px;
    text-align: center;
  }

  &__feedback-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin: 0 auto 12px;
    background-color: #fff;
    border-radius: 50%;
  }

  &__feedback-icon-image {
    width: 26px;
    height: 26px;
  }

  &__feedback-title {
    font-size: 1.125rem;
    font-weight: bold;
    color: $base-font-color;
    margin: 0 0 12px;
  }

  &__feedback-text {
    font-size: 0.875rem;
    color: $base-font-color;
    line-height: 1.6;
    margin: 0 0 16px;
  }

  &__consult-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0 auto;
    min-width: 80%;
  }

  &__consult-button .material-icons {
    font-size: 20px;
  }

  &__close-area {
    padding: 8px 16px 0;
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
  }

  &__error-icon {
    font-size: 48px;
    color: #d32f2f;
    margin-bottom: 16px;

    .material-icons {
      font-size: inherit;
    }
  }

  &__error-message {
    font-size: 1rem;
    color: $base-font-color;
    margin: 0 0 24px;
    line-height: 1.6;
  }

  &--empty {
    display: flex;
    flex-direction: column;
  }

  &__accordion-wrapper {
    padding: 0 16px 16px;
  }
}

.back-link {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  width: fit-content;
  padding: 8px 0;
  font-size: 0.875rem;
  color: $tertiary-color;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: $primary-color;
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 2px;
    border-radius: 4px;
  }
}
</style>
